import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Trash2 } from "lucide-react";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Textarea } from "@/shared/ui/textarea.jsx";
import { Calendar } from "@/shared/ui/calendar.jsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import { cn } from "@/shared/lib/cn.js";
import { formatDateTime } from "@/shared/lib/format.js";
import { NOTE_TYPES, NOTE_TYPE_IDS } from "../lead-constants.js";
import { useAddNote, useDeleteNote, useLeadNotes } from "../use-leads.js";

/**
 * Aba de histórico e anotações do lead.
 *
 * As anotações vão para o banco. Antes ficavam numa chave do `localStorage`
 * chamada `dentari:crm:notes` — trocar de navegador ou limpar o cache
 * apagava o histórico inteiro da clínica.
 *
 * @param {{ leadId: string }} props
 */
export function LeadNotesTab({ leadId }) {
  const { data: anotacoes = [], isLoading } = useLeadNotes(leadId);
  const adicionar = useAddNote();
  const remover = useDeleteNote();

  const [texto, setTexto] = useState("");
  const [tipo, setTipo] = useState("observacao");
  const [dataRetorno, setDataRetorno] = useState(undefined);

  const precisaDeData = tipo === "retorno";
  const podeSalvar = texto.trim() && (!precisaDeData || dataRetorno);

  function salvar() {
    if (!podeSalvar) return;
    adicionar.mutate(
      {
        leadId,
        type: tipo,
        text: texto.trim(),
        reminderAt: precisaDeData ? comHorarioComercial(dataRetorno) : null,
      },
      {
        onSuccess: () => {
          setTexto("");
          setDataRetorno(undefined);
        },
      },
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* ---------------------------------------------------- formulário */}
      <div className="border-b border-border/60 p-5">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {NOTE_TYPE_IDS.map((id) => {
            const meta = NOTE_TYPES[id];
            const ativo = tipo === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTipo(id)}
                aria-pressed={ativo}
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors",
                  ativo ? meta.tone : "bg-muted text-muted-foreground hover:bg-muted/70",
                )}
              >
                <meta.icon className="h-3 w-3" />
                {meta.label}
              </button>
            );
          })}
        </div>

        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={
            precisaDeData
              ? "Ex: Cliente pediu para retornar dia 25 para fechar o orçamento..."
              : `Escreva ${NOTE_TYPES[tipo].label.toLowerCase()}...`
          }
          maxLength={2000}
          rows={3}
          aria-label="Texto da anotação"
        />

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            {precisaDeData && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !dataRetorno && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dataRetorno
                      ? format(dataRetorno, "PPP", { locale: ptBR })
                      : "Selecione a data de retorno"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dataRetorno} onSelect={setDataRetorno} autoFocus />
                </PopoverContent>
              </Popover>
            )}
          </div>

          <Button size="sm" onClick={salvar} disabled={!podeSalvar || adicionar.isPending}>
            {adicionar.isPending ? <Spinner /> : null}
            Adicionar anotação
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------ histórico */}
      <div className="scrollbar-slim flex-1 space-y-3 overflow-y-auto p-5">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-5 w-5 text-muted-foreground" />
          </div>
        ) : anotacoes.length === 0 ? (
          <p className="text-center text-xs text-muted-foreground">Nenhuma anotação ainda.</p>
        ) : (
          anotacoes.map((nota) => {
            const meta = NOTE_TYPES[nota.type] ?? NOTE_TYPES.observacao;
            return (
              <Card key={nota.id} className="group p-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="secondary" className={cn("text-[10px]", meta.tone)}>
                    <meta.icon className="mr-1 h-2.5 w-2.5" />
                    {meta.label}
                  </Badge>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">
                      {formatDateTime(nota.createdAt)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={() => remover.mutate({ noteId: nota.id, leadId })}
                      aria-label="Remover anotação"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>

                <p className="mt-2 whitespace-pre-wrap text-sm">{nota.text}</p>

                {nota.reminderAt && (
                  <div className="mt-2 flex items-center gap-1.5 rounded-md bg-chart-3/5 p-1.5 text-[11px] font-medium text-chart-3">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Retorno: {format(new Date(nota.reminderAt), "PPP", { locale: ptBR })}
                  </div>
                )}

                {nota.author && (
                  <p className="mt-2 text-[10px] text-muted-foreground">por {nota.author}</p>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

/** O calendário devolve meia-noite; o compromisso vale para as 9h. */
function comHorarioComercial(data) {
  const d = new Date(data);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}
